import NoteRichEditor from './NoteRichEditor.jsx';
// src/components/ReaderNotesDrawer.jsx
//
// Per-reader NOTE panel for the active content item. Mirrors ReaderAiDrawer's mount: render
// nothing when closed; when open, a portal to document.body holds a dim backdrop + a fixed
// width-capped panel (so a lingering .page-fade-in transform can't break position:fixed).
// Re-applies the day/night skin from ReadingPrefsContext (portals preserve React context).
//
// Loads + saves the single note for (packId,itemId) via useNote (Firestore, owner-only,
// serverTimestamp writes per firestore.rules). The whole panel is `.selectable` so the
// reader content-protection (useContentProtection) does NOT block the user typing/copying
// their OWN note (analysis content stays blocked).
import React, { useEffect, useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import { printNote } from '../utils/printNote.js';
import { IconX } from './icons.jsx';
import { ReadingPrefsContext } from '../pages/useReadingPrefs.js';
import { portalSkinClass } from '../pages/readingPrefs.js';
import { useNote } from '../storage/useUserContent.js';

export default function ReaderNotesDrawer({ open, onClose, packId, itemId, itemType, title }) {
    const { colorMode } = useContext(ReadingPrefsContext);
    const { note, loading, uid, saveNote, clearNote } = useNote(packId, itemId);

    const [draft, setDraft] = useState('');
    const [status, setStatus] = useState(null); // 'saving' | 'saved' | 'error' | null

    // Sync the editor to the loaded note whenever the panel opens or the item changes.
    useEffect(() => {
        if (open) { setDraft((note && note.body) || ''); setStatus(null); }
    }, [open, itemId, note]);

    // Esc closes (listener only while open).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open || typeof document === 'undefined') return null;

    const skin = portalSkinClass(colorMode);

    const onSave = async () => {
        setStatus('saving');
        try { await saveNote(draft, { itemType: itemType || null, title: title || null }); setStatus('saved'); }
        catch { setStatus('error'); }
    };
    const onClear = async () => {
        try { await clearNote(); setDraft(''); setStatus(null); }
        catch { setStatus('error'); }
    };

    return createPortal(
        <div className={`selectable ${skin}`}>
            {/* Backdrop: same translucent scrim approach as the AI drawer (inline colour so the
                day skin doesn't remap it to opaque cream). Click outside closes. */}
            <div className="fixed inset-0 z-[60]" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} onClick={onClose} aria-hidden="true" />

            <div
                className="fixed right-0 bottom-0 top-16 z-[61] flex flex-col w-full sm:w-[24rem] lg:w-[28rem] max-w-[100vw] box-border bg-book-pageDark border-l border-book-accent/30 shadow-[-10px_0_30px_rgba(0,0,0,0.45)]"
                role="dialog" aria-modal="true" aria-label="My Notes">
                <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-book-accent/20">
                    <div className="min-w-0">
                        <div className="font-display text-book-accent text-xs font-semibold">My Notes</div>
                        {title && <div className="font-serif italic text-book-muted text-xs truncate">{title}</div>}
                    </div>
                    <button onClick={onClose} type="button" aria-label="Close notes"
                        className="w-11 h-11 flex items-center justify-center text-book-muted hover:text-book-accent transition-colors flex-shrink-0">
                        <IconX className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 min-h-0 flex flex-col p-3 gap-3">
                    {!uid ? (
                        <p className="font-serif italic text-book-muted text-sm">Sign in to keep private notes on this item.</p>
                    ) : (
                        <>
                            <NoteRichEditor value={draft} onChange={v => { setDraft(v); setStatus(null); }} disabled={loading} />
                            <div className="flex items-center justify-between gap-3 flex-shrink-0">
                                <div className="font-serif italic text-book-muted text-xs">
                                    {status === 'saving' ? 'Saving…'
                                        : status === 'saved' ? 'Saved.'
                                        : status === 'print-blocked' ? 'Allow pop-ups to print.'
                                        : status === 'error' ? <span className="text-red-300/80">Could not save.</span>
                                        : `${draft.length}/5000`}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => { if (!printNote(title, draft)) setStatus('print-blocked'); }} disabled={loading || !draft.trim()} className="px-3 py-1.5 border border-book-accent/30 rounded-full text-sm disabled:opacity-40">Print / PDF</button>
                                    <button onClick={onClear} disabled={loading || (!note && draft.length === 0)}
                                        className="px-3 py-1.5 border border-book-accent/30 text-book-muted font-display text-sm hover:text-book-accent disabled:opacity-40 transition-colors font-medium rounded-full">
                                        Clear
                                    </button>
                                    <button onClick={onSave} disabled={loading}
                                        className="px-4 py-1.5 bg-book-accent text-book-pageDark font-display text-sm hover:bg-book-accentLight disabled:opacity-40 transition-colors font-semibold">
                                        Save
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
